// component.jsx — CardClientComponent (UI; se oculta si no hay client)
import {
  Box, Typography, List, ListItem, ListItemText, ListItemIcon,
  Chip, Divider, Card, CardContent
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import { useCardClient } from "./store";
import { UserAvatar } from "../../../elements/UserAvatar";

const StyledCardClient = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const CardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
}));

const ClientHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ClientInfo = styled(Box)(() => ({ flex: 1 }));

const InfoList = styled(List)(({ theme }) => ({
  "& .MuiListItem-root": { padding: theme.spacing(0.5, 0) },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

export const CardClientComponent = () => {
  const { client, role } = useCardClient();
  if (!client) return null;

  return (
    <StyledCardClient elevation={1}>
      {/* Header primary */}
      <Box
        sx={(t) => ({
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "& .title": { fontWeight: 700, color: "inherit" },
          "& .sub":   { color: t.palette.primary.contrastText, opacity: 0.85 },
        })}
      >
        <Box>
          <Typography className="title" variant="h6">Cliente</Typography>
          <Typography className="sub" variant="body2">{client.company || "Sin empresa"}</Typography>
        </Box>
      </Box>

      <CardContent>
        <CardContainer>
          <ClientHeader>
            <UserAvatar name={client.name} size="medium" color="primary" />
            <ClientInfo>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {client.name || "Cliente sin nombre"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client.company || "Sin empresa"}
              </Typography>
              <StatusChip
                label={client.status || "Activo"}
                size="small"
                color={client.status === "active" ? "success" : "default"}
                variant="outlined"
              />
            </ClientInfo>
          </ClientHeader>

          <Divider sx={{ my: 1 }} />

          <InfoList dense>
            {client.email && (
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <EmailIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Email"
                  secondary={client.email}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            )}

            {client.phone && (
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PhoneIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Teléfono"
                  secondary={client.phone}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            )}

            {client.company && (
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <BusinessIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Empresa"
                  secondary={client.company}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            )}

            {client.address && (
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <BusinessIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Dirección"
                  secondary={client.address}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            )}
          </InfoList>

          {role === "adevs-admin" && client.id && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                ID Cliente: {client.id}
              </Typography>
            </Box>
          )}
        </CardContainer>
      </CardContent>
    </StyledCardClient>
  );
};
